import { isRollCarpetFinish } from './room-finish.js';
const finite = (n) => Number.isFinite(n) && n >= 0;
const round = (n, d = 2) => Number(n.toFixed(d));
export function sourceReview(floor, sourceRevisionId) {
    const p = floor.carpetPlan;
    return { floorId: floor.id, sourceRevisionId, rollWidthM: p?.rollWidthM ?? 3.66, direction: p?.direction ?? 'topBottom', allowanceM: p?.allowanceM ?? 0.1, cuts: structuredClone(p?.cuts ?? []), notes: '', annotations: [] };
}
export function evaluateCarpetRun(floor, review) {
    const checks = [];
    const carpet = floor.areas.filter(a => isRollCarpetFinish(a.finish));
    const names = new Set(carpet.map(a => a.name));
    if (!carpet.length)
        checks.push({ code: 'no_rooms', message: 'No carpet rooms are marked on this floor.', blocking: true });
    if (!finite(review.rollWidthM) || review.rollWidthM <= 0)
        checks.push({ code: 'roll_width', message: 'Set a positive roll width.', blocking: true });
    if (!finite(review.allowanceM) || review.allowanceM < 0)
        checks.push({ code: 'allowance', message: 'CarpetRunCut allowance is invalid.', blocking: true });
    if (floor.carpetPlan?.asCut)
        checks.push({ code: 'as_cut', message: 'The source is an as-cut record. CarpetRunReview it as installed evidence, not an order proposal.', blocking: true });
    if (!review.cuts.length)
        checks.push({ code: 'no_cuts', message: 'No physical roll cuts are planned.', blocking: true });
    const covered = new Set();
    let lm = 0, usedM2 = 0;
    for (const [ci, cut] of review.cuts.entries()) {
        if (!finite(cut.lengthM) || cut.lengthM <= 0) {
            checks.push({ code: 'cut_length', message: `CarpetRunCut ${ci + 1} has no valid length.`, blocking: true });
            continue;
        }
        lm += cut.lengthM;
        if (!cut.pieces.length)
            checks.push({ code: 'empty_cut', message: `CarpetRunCut ${ci + 1} has no pieces.`, blocking: true });
        for (const [pi, p] of cut.pieces.entries()) {
            const label = `CarpetRunCut ${ci + 1}, piece ${pi + 1}`;
            if (!names.has(p.room))
                checks.push({ code: 'room', message: `${label} names a room outside this floor's carpet scope: ${p.room}.`, blocking: true });
            else
                covered.add(p.room);
            if (![p.widthM, p.lengthM, p.acrossM, p.offsetM].every(finite) || p.widthM <= 0 || p.lengthM <= 0)
                checks.push({ code: 'piece_size', message: `${label} has an invalid size or position.`, blocking: true });
            if (p.acrossM + p.widthM > review.rollWidthM + 0.005)
                checks.push({ code: 'piece_width', message: `${label} runs past the roll width.`, blocking: true });
            if (p.offsetM + p.lengthM > cut.lengthM - review.allowanceM + 0.015)
                checks.push({ code: 'piece_length', message: `${label} exceeds usable cut length after its one allowance.`, blocking: true });
            usedM2 += Math.max(0, p.widthM * p.lengthM);
        }
        for (let a = 0; a < cut.pieces.length; a++)
            for (let b = a + 1; b < cut.pieces.length; b++) {
                const x = cut.pieces[a], y = cut.pieces[b];
                if (x.acrossM < y.acrossM + y.widthM - 0.005 && y.acrossM < x.acrossM + x.widthM - 0.005 && x.offsetM < y.offsetM + y.lengthM - 0.005 && y.offsetM < x.offsetM + x.lengthM - 0.005)
                    checks.push({ code: 'overlap', message: `CarpetRunCut ${ci + 1}: ${x.name} and ${y.name} overlap on the roll.`, blocking: true });
            }
    }
    for (const room of carpet)
        if (!covered.has(room.name))
            checks.push({ code: 'uncovered', message: `${room.name} has no assigned piece.`, blocking: true });
    const scale = floor.scaleMmPerPx;
    for (const room of carpet) {
        for (const m of room.measures ?? []) {
            if (!scale || !m.pxRun || !m.lengthMM)
                continue;
            const expected = m.pxRun * scale;
            const delta = Math.abs(expected - m.lengthMM);
            if (delta > Math.max(150, expected * 0.08))
                checks.push({ code: delta > Math.max(500, expected * 0.5) ? 'measure_conflict' : 'scale_discrepancy', message: `${room.name}: ${m.label ?? m.axis ?? 'measure'} is ${Math.round(m.lengthMM)} mm, while the traced scale gives about ${Math.round(expected)} mm. CarpetRunCheck the anchor against the laser reading.`, blocking: delta > Math.max(500, expected * 0.5) });
        }
    }
    const stairs = (floor.stairs ?? []).filter(s => isRollCarpetFinish(s.finish));
    if (stairs.length)
        checks.push({ code: 'stairs', message: `${stairs.reduce((n, s) => n + (s.steps ?? 0), 0)} carpet steps are separate from these floor cuts. Confirm tread, riser, lip and stair width before ordering.`, blocking: true });
    const laidM2 = scale ? carpet.reduce((n, a) => n + (a.pxArea ?? 0) * (scale / 1000) ** 2, 0) : null;
    const orderM2 = lm * review.rollWidthM;
    if (laidM2 != null && usedM2 + 0.05 < laidM2)
        checks.push({ code: 'coverage', message: `Pieces cover ${round(usedM2)} m² on the roll versus about ${round(laidM2)} m² of marked carpet floor. CarpetRunCheck joins, shapes and open edges.`, blocking: true });
    return { checks, lm: round(lm, 3), orderM2: round(orderM2, 3), usedM2: round(usedM2, 3), laidM2: laidM2 == null ? null : round(laidM2, 3), spareM2: round(Math.max(0, orderM2 - usedM2), 3), ready: checks.every(c => !c.blocking) };
}
/** The carpet rooms of a floor as a reviewer saw them: id and outline, to 0.1 px. */
export function carpetRoomShapes(floor) {
    return floor.areas.filter(a => isRollCarpetFinish(a.finish)).map(a => ({ id: a.id.toLowerCase(), boundary: a.boundary.map(p => ({ x: Math.round(p.x * 10) / 10, y: Math.round(p.y * 10) / 10 })) }))
        .sort((a, b) => a.id.localeCompare(b.id));
}
const sameShapes = (a, b) => a.length === b.length && a.every((r, i) => {
    const o = b[i];
    return r.id === o.id && r.boundary.length === o.boundary.length && r.boundary.every((p, j) => Math.abs(p.x - o.boundary[j].x) < 0.15 && Math.abs(p.y - o.boundary[j].y) < 0.15);
});
/**
 * A confirmed review counts only while it still describes the floor: it passes every check above and the carpet rooms
 * are the ones that were confirmed. Redraw a carpet room after confirming and the review falls back to provisional —
 * a newer save carries the old review forward untouched (quantities pass through), so without this a stale cut plan
 * would read as current (Jacky, 18 Oct 2026: "my iPad confirm counts as reviewed").
 */
export function carpetReviewCurrent(floor, review) {
    if (!review.reviewed)
        return false;
    if (!evaluateCarpetRun(floor, review).ready)
        return false;
    const was = [...review.reviewed.rooms].map(r => ({ id: r.id.toLowerCase(), boundary: r.boundary })).sort((a, b) => a.id.localeCompare(b.id));
    return sameShapes(carpetRoomShapes(floor), was);
}
/** Stamp a review as confirmed by this person now, with the rooms it was checked against. Refused if it does not pass. */
export function confirmCarpetReview(floor, review, by, surface, at = new Date()) {
    const result = evaluateCarpetRun(floor, review);
    if (!result.ready)
        throw new Error(result.checks.filter(c => c.blocking).map(c => c.message).join(' '));
    return { ...review, reviewed: { by, at: at.toISOString(), surface, rooms: carpetRoomShapes(floor) } };
}
//# sourceMappingURL=carpet-run.js.map